import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import { fetchClients } from "../../../../Slices/clientSlice";
import { S3ACCESSURL } from "../../../../databucket";
import { CustomGridTable } from "../../../../blueplanitUtils/CustomGridTable";

const AllInvoices = () => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(fetchClients());
    }, [dispatch]);

    const clientsData = useSelector(
        (state) => state?.clients?.clients ?? []
    );

    const invoices = useMemo(() => {
        return clientsData.flatMap((client) =>
            (client.GeneratedInvoices ?? []).map((inv) => ({
                ...inv,
                clientName: client.clientName,
                invoiceDetails: client.InvoiceDetails ?? [],
            }))
        );
    }, [clientsData]);

    const handleDownload = async (url, invoiceNo) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();

            const ext = blob.type === "application/pdf" ? "pdf" : blob.type.split("/")[1] || "file";
            const filename = `Invoice-${invoiceNo}.${ext}`;

            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = filename;

            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download failed", err);
            alert("Unable to download file");
        }
    };

    const invoiceColumns = [
        {
            title: "Invoice No",
            dataIndex: "invoiceNo",
            key: "invoiceNo",
            align: "center",
            render: (_, record, index) =>
                record.invoiceNo || `INV-${index + 1}`,
        },
        {
            title: "Subscription No",
            key: "subscriptionNo",
            align: "center",
            render: (_, inv) =>
                Number.isInteger(inv.subscriptionIndex)
                    ? `Subscription - ${inv.subscriptionIndex + 1}`
                    : "--",
        },
        {
            title: "Client Name",
            dataIndex: "clientName",
            key: "clientName",
            align: "center",
            render: (text) => text ?? "--",
        },
        {
            title: "Billing Start Date",
            key: "billingStartDate",
            align: "center",
            render: (_, inv) => {
                return inv.subscriptionIndex === null
                    ? dayjs(Number(inv.invoiceDate)).format("MMM DD, YYYY") // ONE-OFF
                    : typeof inv.subscriptionIndex === "number" &&
                        inv.invoiceDetails?.[inv.subscriptionIndex]?.billStarting
                        ? dayjs(
                            Number(
                                inv.invoiceDetails[inv.subscriptionIndex].billStarting
                            )
                        ).format("MMM DD, YYYY")
                        : "--";
            },
        },
        {
            title: "Invoice Generated Date",
            key: "invoiceDate",
            align: "center",
            render: (_, inv) =>
                inv.invoiceDate
                    ? dayjs(Number(inv.invoiceDate)).format("MMM DD, YYYY")
                    : "--",
        },
        {
            title: "Sub Total",
            dataIndex: "subTotal",
            key: "subTotal",
            align: "center",
            render: (value) => `$${(value ?? 0).toFixed(2)}`,
        },
        {
            title: "Tax",
            dataIndex: "taxTotal",
            key: "taxTotal",
            align: "center",
            render: (value) => `$${(value ?? 0).toFixed(2)}`,
        },
        {
            title: "Grand Total",
            dataIndex: "grandTotal",
            key: "grandTotal",
            align: "center",
            render: (value) => `$${(value ?? 0).toFixed(2)}`,
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (_, inv, index) => {
                const invoiceNo = inv.invoiceNo || `INV-${index + 1}`;
                return inv.invoiceImage ? (
                    <>
                        <a
                            href={S3ACCESSURL + inv.invoiceImage}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ marginRight: 8 }}
                        >
                            Preview
                        </a>
                        <a
                            style={{ cursor: "pointer", color: "#1677ff" }}
                            onClick={() =>
                                handleDownload(
                                    S3ACCESSURL + inv.invoiceImage,
                                    `Invoice-${invoiceNo}.pdf`
                                )
                            }
                        >
                            Download
                        </a>
                    </>
                ) : (
                    <span style={{ color: "#999", fontSize: 12 }}>
                        No PDF
                    </span>
                );
            },
        },
    ];


    return (
        <div className="card mb-3" style={{ marginTop: 20, padding: "12px 16px" }}>
            <h5 className="mb-3" style={{ fontSize: 15, fontWeight: 600 }}>
                Generated Invoices
            </h5>

            {invoices.length === 0 ? (
                <div style={{ fontSize: 13, color: "#999" }}>
                    No invoices found.
                </div>
            ) : (
                <CustomGridTable
                    Id="generated-invoices-table-for-all"
                    columns={invoiceColumns}
                    dataSource={invoices}
                    rowKey={(record, index) => record.invoiceNo ?? index}
                    pagination={false}
                    size="small"
                    enableColumnResize
                    enableColumnToggle
                    disableColumnDrag={false}
                />
            )}
        </div>

    );
};

export default AllInvoices;
